import {
  Box,
  Button,
  Checkbox,
  Grid,
  TextField,
} from '@material-ui/core';
import { Autocomplete } from '@material-ui/lab';
import React, { useMemo, useState } from 'react';
import CheckBoxIcon from '@material-ui/icons/CheckBox';
import CheckBoxOutlineBlankIcon from '@material-ui/icons/CheckBoxOutlineBlank';
import DrawerToolbar from 'common/components/drawer/drawer-toolbar';
import { AutomationFilter, DepartmentResponse, GetWorkOrderStatusesResponse } from '../../api/automation-api';
import { Drawer } from '@material-ui/core';
import { AutomationType } from 'components/automation/api/automation-constants';
import { LocationOption } from 'common/models/location-option';
import { FilterOption } from 'common/utils/shared/shared-models';
import { formatPhoneNumber } from 'common/utils';

const icon = <CheckBoxOutlineBlankIcon fontSize="small" />;
const checkedIcon = <CheckBoxIcon fontSize="small" />;

const statusOptions:FilterOption[]  = [{ label: "Active", value: 1 }, { label: "Inactive", value: 0 }];

const automationTypeOptions : FilterOption[] = Object.keys(AutomationType)
    .filter((key) => isNaN(Number(key)))
    .map((key) => ({
      label: key,
      value: AutomationType[key as keyof typeof AutomationType],
    }));


interface AutomationFilterDrawerProps {
  allLocations?: LocationOption[];
  dataWOStatus: Array<GetWorkOrderStatusesResponse> | undefined;
  allDepartments: Array<DepartmentResponse>;
  automationFilters: AutomationFilter;
  onSubmit: (values: any) => void;
  onclose: () => void;
};


const AutomationFilterDrawer: React.FC<AutomationFilterDrawerProps> = ({
  allLocations,
  dataWOStatus,
  allDepartments,
  automationFilters,
  onSubmit,
  onclose
}) => {
  
  const departmentOptions: FilterOption[] = useMemo(() => {
    return Array.from(
      new Map(
        allDepartments
          ?.flatMap(d => d.Departments || [])
          .map(dept => [dept.departmentId, dept]) // Deduplicate by DepartmentId
      ).values()
    ).map(dept => ({
      label: dept.departmentName +" " + formatPhoneNumber(dept.phoneNumber) ,
      value: dept.departmentId,
    }))||[];
  }, [allDepartments]);


  const locationOptions: FilterOption[] = useMemo(() => {
    return allLocations?.map(loc => ({
      label: loc.name,
      value: loc.dealerLocationId,
    }))||[];
  }, [allDepartments]);

  const workOrderOptions: FilterOption[] = useMemo(() => {
    return dataWOStatus?.map(wo => ({
      label: wo.jobStatus,
      value: wo.jobStatusId,
    }))||[];
  }, [dataWOStatus]);

  const [autoTypeSelected, setAutoTypeSelected] = useState<FilterOption[]>(
    automationTypeOptions.filter(opt => automationFilters.automationTypeIds?.includes(opt.value as number))
  );
  
  const [locationSelected, setLocationSelected] = useState<FilterOption[]>(
    locationOptions.filter(opt => automationFilters.locationDealerIds?.includes(opt.value as number))
  );
  
  const [statusSelected, setStatusSelected] = useState<FilterOption[]>(
    statusOptions.filter(opt => automationFilters.statusIds?.includes(opt.value as number))
  );
  
  const [workOrderSelected, setWorkOrderSelected] = useState<FilterOption[]>(
    workOrderOptions.filter(opt => automationFilters.workOrderStatusIds?.includes(opt.value as string))
  );
  
  const [departmentSelected, setDepartmentSelected] = useState<FilterOption[]>(
    departmentOptions.filter(opt => automationFilters.departmentIds?.includes(opt.value as number))
  );
  
  const [allAutoTypeSelected, setAllAutoTypeSelected] = useState(automationFilters.automationTypeIds?.length === automationTypeOptions.length);


  const handleTypeChange = (_: any, selectedOptions: FilterOption[]) => {
    setAutoTypeSelected(selectedOptions);
  };

  const handleLocationChange = (_: any, selectedOptions: FilterOption[]) => {
    setLocationSelected(selectedOptions);
  };

  const handleDepartmentChange = (_: any, selectedOptions: FilterOption[]) => {
  
    setDepartmentSelected(selectedOptions);
  };

  const handleStatusChange = (_: any, selectedOptions: FilterOption[]) => {
  
    setStatusSelected(selectedOptions);
  };

  const handleWorkOrderChange = (_: any, selectedOptions: FilterOption[]) => {
  
    setWorkOrderSelected(selectedOptions);
  };

  const handleApplyFilter = async () => {
    const filterData: AutomationFilter = {
      departmentIds: departmentSelected.map((x) => x.value as number),
      workOrderStatusIds: workOrderSelected.map((x) => x.value as string),
      statusIds: statusSelected.map((x) => x.value as number),
      locationDealerIds: locationSelected.map((x) => x.value as number),
      automationTypeIds: autoTypeSelected.map((x) => x.value as number)
      };

    onSubmit(filterData);
}

  return (
    <>
      <Drawer
        style={{ width: '450px' }}
        anchor="right"
        variant="temporary"
        open={true}
        PaperProps={{ style: { width: '450px' } }}
        onClose={onclose}
        SlideProps={{ timeout: { enter: 300, exit: 300 } }}>

        <DrawerToolbar title={'Filters'} onClose={onclose} />

        <Grid container>
          <Box mt={2} mb={2} ml={2} mr={2} width={'100%'}>
          <Grid item xs={12}>
            <Autocomplete
              id="locationsDropdown"
              value={locationSelected || []}
              multiple={true}
              options={locationOptions || []}
              onChange={handleLocationChange}
              getOptionLabel={(option: FilterOption) => option.label}
              disableCloseOnSelect
              getOptionSelected={(option: FilterOption, value: FilterOption): boolean => {
                return option.value === value.value;
              }}
              renderOption={(option, { selected }) => (
                <>
                  <Checkbox icon={icon} checkedIcon={checkedIcon} checked={selected} />
                  {option.label}
                </>
              )}
              renderInput={(params) => (
                <TextField
                  {...params}
                  name="locations"
                  id="locations"
                  variant="outlined"
                  margin="dense"
                  label="Location"
                  placeholder="Select Location(s)"
                />
              )}
            />
          </Grid>
          <Grid item xs={12}>
            <Autocomplete
              id="departmentDropdown"
              value={departmentSelected || []}
              multiple={true}
              options={departmentOptions || []}
              onChange={handleDepartmentChange}
              getOptionLabel={(option: any) => option.label}
              disableCloseOnSelect
              getOptionSelected={(option: FilterOption, value: FilterOption): boolean => {
                return option.value === value.value;
              }}
              renderOption={(option, { selected }) => (
                <>
                  <Checkbox icon={icon} checkedIcon={checkedIcon} checked={selected} />
                  {option.label}
                </>
              )}
              renderInput={(params) => (
                <TextField
                  {...params}
                  name="department"
                  id="department"
                  variant="outlined"
                  margin="dense"
                  label="Department"
                  placeholder="Select Department(s)"
                />
              )}
            />
          </Grid>

          <Grid item xs={12}>
            <Autocomplete
              id="workOrderDropdown"
              value={workOrderSelected || []}
              multiple={true}
              options={workOrderOptions || []}
              onChange={handleWorkOrderChange}
              getOptionLabel={(option: FilterOption) => option.label}
              disableCloseOnSelect
              getOptionSelected={(option: FilterOption, value: FilterOption): boolean => {
                return option.value === value.value;
              }}
              renderOption={(option, { selected }) => (
                <>
                  <Checkbox icon={icon} checkedIcon={checkedIcon} checked={selected} />
                  {option.label}
                </>
              )}
              renderInput={(params) => (
                <TextField
                  {...params}
                  name="workOrder"
                  id="workOrder"
                  variant="outlined"
                  margin="dense"
                  label="Trigger"
                  placeholder="Select Trigger(s)"
                />
              )}
            />
          </Grid>

          <Grid item xs={12}>
            <Autocomplete
              id="autoTypeDropdown"
              value={autoTypeSelected || []}
              multiple={true}
              options={automationTypeOptions || []}
              onChange={handleTypeChange}
              getOptionLabel={(option: FilterOption) => option.label}
              disableCloseOnSelect
              getOptionSelected={(option: FilterOption, value: FilterOption): boolean => {
                return option.value === value.value;
              }}
              renderOption={(option, { selected }) => (
                <>
                 <Checkbox icon={icon} checkedIcon={checkedIcon} checked={selected} />
                  {option.label}
                </>
              )}
              renderInput={(params) => (
                <TextField
                  {...params}
                  name="autoType"
                  id="autoType"
                  variant="outlined"
                  margin="dense"
                  label="Automation Types"
                  placeholder="Select type(s)"
                />
              )}
            />
          </Grid>

          <Grid item xs={12}>
            <Autocomplete
              id="statusDropdown"
              value={statusSelected || []}
              multiple={true}
              options={statusOptions || []}
              onChange={handleStatusChange}
              getOptionLabel={(option: FilterOption) => option.label}
              disableCloseOnSelect
              getOptionSelected={(option: FilterOption, value: FilterOption): boolean => {
                return option.value === value.value;
              }}
              renderOption={(option, { selected }) => (
                <>
                  <Checkbox icon={icon} checkedIcon={checkedIcon} 
                  checked={selected}
                   />
                  {option.label}
                </>
              )}
              renderInput={(params) => (
                <TextField
                  {...params}
                  name="status"
                  id="status"
                  variant="outlined"
                  margin="dense"
                  label="Status"
                  placeholder="Select Status(es)"
                />
              )}
            />
          </Grid>

          <Grid item xs={12} style={{ marginTop: '20px' }}>
            {/* <Grid item style={{ float: 'right' }}>
              <Button
                color="secondary"
                variant="contained"
                size="small"
                onClick={() => {
                  //   clearFiltersHanlder(true);
                }}>
                Reset Filters
              </Button>
            </Grid> */}
            <Grid item style={{ float: 'right' }}>
              <Button color="primary" variant="contained" size="small"
              onClick={handleApplyFilter}
              >
                Apply Filters
              </Button>
            </Grid>
          </Grid>
          </Box>
          
        </Grid>
      </Drawer>
    </>
  );
}

export default AutomationFilterDrawer;
